'use client';

import type { ErrorInfo } from 'react';
import { AlertTriangle, Copy, House, RefreshCw, RotateCcw, Trash2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/cn';
import { toast } from '@/lib/stores/toast-store';

export interface RuntimeErrorCardProps {
  error?: Error;
  errorInfo?: ErrorInfo;
  errorCount?: number;
  recoveryId?: string;
  isRepeatedError?: boolean;
  layout?: 'embedded' | 'fullscreen';
  context?: 'app' | 'workspace';
  onRetry?: () => void;
  onRefresh?: () => void;
  onClearCache?: () => void;
  onGoToExplore?: () => void;
}

export function RuntimeErrorCard({
  error,
  errorInfo,
  errorCount = 0,
  recoveryId,
  isRepeatedError = false,
  layout = 'embedded',
  context = 'app',
  onRetry,
  onRefresh,
  onClearCache,
  onGoToExplore,
}: RuntimeErrorCardProps) {
  const t = useTranslations('errors');

  const title = isRepeatedError
    ? t('repeatedErrors')
    : context === 'workspace'
      ? t('workspaceErrorTitle')
      : t('somethingWentWrong');
  const description = isRepeatedError
    ? t('repeatedErrorMessage')
    : context === 'workspace'
      ? t('workspaceError')
      : t('appError');
  const resolvedRecoveryId = recoveryId || (error as (Error & { digest?: string }) | undefined)?.digest;
  const handleGoToExplore = onGoToExplore ?? (() => {
    window.location.assign('/explore');
  });

  return (
    <div
      translate="no"
      className={cn(
        'notranslate relative flex items-center justify-center overflow-auto bg-[#F2F3F5] px-4 py-8 font-sans text-[#203644] [&_button]:min-h-11 [&_button:focus-visible]:outline [&_button:focus-visible]:outline-2 [&_button:focus-visible]:outline-offset-4 [&_button:focus-visible]:outline-[#1787B2]',
        layout === 'fullscreen' ? 'min-h-screen' : 'h-full w-full min-h-0',
      )}
    >
      <div data-studio-surface className="w-full max-w-xl rounded-[18px] border border-[#D9E0E3] bg-white/96 p-6 shadow-[0_24px_64px_rgba(32,54,68,0.12)] backdrop-blur-xl sm:p-8">
        <div className="space-y-6">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-600 shadow-[0_14px_32px_rgba(245,158,11,0.18)]">
            <AlertTriangle className="h-7 w-7" />
          </div>

          <div className="space-y-3 text-center">
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-600/80">
                {t('recoveryEyebrow')}
              </p>
              <h2 className="text-2xl font-semibold tracking-tight text-[#203644]">
                {title}
              </h2>
            </div>
            <p className="mx-auto max-w-md text-sm leading-6 text-slate-600">
              {description}
            </p>
            <p className="mx-auto max-w-md text-xs leading-5 text-slate-500">
              {t('recoverySafetyNote')}
            </p>
            {resolvedRecoveryId ? (
              <p className="mx-auto max-w-md text-xs leading-5 text-slate-500">
                {t('recoveryLogged', { recoveryId: resolvedRecoveryId })}
              </p>
            ) : null}
            {isRepeatedError ? (
              <p className="text-xs font-medium text-slate-500">
                {t('errorCount', { count: errorCount })}
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            {onRefresh ? (
              <button
                type="button"
                onClick={onRefresh}
                className="inline-flex items-center gap-2 rounded-full border border-transparent bg-[#4ABDE9] px-5 py-2.5 text-sm font-semibold text-[#203644] transition-colors hover:bg-[#79CFEE]"
              >
                <RefreshCw className="h-4 w-4" />
                <span>{context === 'workspace' ? t('refreshEditor') : t('refreshPage')}</span>
              </button>
            ) : null}
            {onRetry && !isRepeatedError ? (
              <button
                type="button"
                onClick={onRetry}
                className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                <RotateCcw className="h-4 w-4" />
                <span>{t('retry')}</span>
              </button>
            ) : null}
            <button
              type="button"
              onClick={handleGoToExplore}
              className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              <House className="h-4 w-4" />
              <span>{t('backToExplore')}</span>
            </button>
            {onClearCache ? (
              <button
                type="button"
                onClick={onClearCache}
                className={cn(
                  'inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors',
                  isRepeatedError
                    ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
                    : 'border-black/10 bg-white text-slate-700 hover:bg-slate-50',
                )}
              >
                <Trash2 className="h-4 w-4" />
                <span>{t('clearCache')}</span>
              </button>
            ) : null}
            {resolvedRecoveryId ? (
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard?.writeText(resolvedRecoveryId);
                  toast.info(t('recoveryIdCopied'));
                }}
                className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2.5 font-mono text-xs font-medium text-slate-500 transition-colors hover:bg-slate-50"
              >
                <Copy className="h-4 w-4" />
                <span>{resolvedRecoveryId}</span>
              </button>
            ) : null}
          </div>

          {process.env.NODE_ENV === 'development' && error ? (
            <details className="rounded-2xl border border-slate-200 bg-slate-950 px-4 py-3 text-left text-slate-100">
              <summary className="cursor-pointer text-sm font-medium text-slate-300">
                {t('errorDetails')}
              </summary>
              <div className="mt-3 space-y-3">
                <div>
                  <p className="mb-1 text-xs text-slate-500">{t('errorMessage')}</p>
                  <pre className="overflow-auto rounded-xl bg-slate-900 p-3 text-xs text-rose-300">
                    {error.toString()}
                  </pre>
                </div>
                {error.stack ? (
                  <div>
                    <p className="mb-1 text-xs text-slate-500">{t('stackTrace')}</p>
                    <pre className="max-h-48 overflow-auto rounded-xl bg-slate-900 p-3 text-xs text-slate-300">
                      {error.stack}
                    </pre>
                  </div>
                ) : null}
                {errorInfo?.componentStack ? (
                  <div>
                    <p className="mb-1 text-xs text-slate-500">{t('componentStack')}</p>
                    <pre className="max-h-48 overflow-auto rounded-xl bg-slate-900 p-3 text-xs text-slate-300">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                ) : null}
              </div>
            </details>
          ) : null}

          {process.env.NODE_ENV === 'development' ? (
            <button
              type="button"
              onClick={() => {
                const logs = localStorage.getItem('error-logs');
                if (logs) {
                  console.log('Error logs:', JSON.parse(logs));
                  toast.info(t('errorLogsPrinted'));
                  return;
                }
                toast.info(t('noErrorLogs'));
              }}
              className="mx-auto block text-xs font-medium text-slate-500 underline decoration-slate-300 underline-offset-4 hover:text-slate-700"
            >
              {t('viewErrorLogs')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
